/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { PublicUser } from "@vienna/common/src";
import type { ActivityType, Page, User } from "@vienna/prisma";

import { Block } from "src/classes/block";
import { BasePlate } from "src/classes/plate/BasePlate/BasePlate";
import { BaseUser } from "src/classes/user";

export interface AuthError {
  code: string | number;
  message: string;
}

export enum LoadingStatus {
  Idle = "idle",
  Loading = "loading",
  LoadingMore = "loadingMore",
  Succeeded = "succeeded",
  Failed = "failed",
}

type UserHit = Pick<User, "username" | "displayName" | "avatarUrl"> & {
  /** the record type matched by search */
  recordType: "user";
};

type PageHit = Pick<Page, "title"> & {
  user: Pick<User, "username" | "displayName" | "avatarUrl">;
} & {
  /** the record type matched by search */
  recordType: "page";
};

/**
 * The hit returned by algolia
 *
 * TODO: fix types elsewhere to avoid any
 */
export type SearchHit = UserHit | PageHit | any;

export interface CSSPositionOffsets {
  top?: number;
  left?: number;
  right?: number;
  bottom?: number;
}

export type ModKey = "ctrl" | "alt" | "shift" | "meta";

//@critical this is bad duplication, but we need a separate client type that knows about classes.
interface NotificationBase {
  type: ActivityType;
  id: string; // the id of the activity
  user: BaseUser; // the user who did the activity (TODO, this should be changed to the user who is receiving the notification)
  startTime: Date;
  endTime: Date;
}

export interface FollowNotification extends NotificationBase {
  type: typeof ActivityType.FOLLOW;
  userFollowed: PublicUser;
}

export interface PlateShareNotification extends NotificationBase {
  type: typeof ActivityType.PAGE_SHARED;
  plate: BasePlate;
}

export interface LikeNotification extends NotificationBase {
  type: typeof ActivityType.BLOCK_OR_PAGE_LIKE;
  block?: Block;
  plate?: BasePlate;
}

export type Notification = FollowNotification | PlateShareNotification | LikeNotification;
